import { useEffect } from "react";

const SITE_NAME = "Recette-Diabete";
const SITE_URL = "https://recette-diabete.com";

/** Dynamic SEO head tags for SPA pages.
 * Sets: document.title, meta description, keywords, og/twitter cards, canonical link,
 * and an optional JSON-LD script.
 *
 * Props:
 *   title       — Page title (will be suffixed with " · Recette-Diabete")
 *   description — Meta description (160 chars max recommended)
 *   keywords    — Array or comma string
 *   image       — Absolute URL OR data URL OR path for og:image / twitter:image
 *   path        — Pathname for canonical (defaults to current pathname)
 *   type        — og:type (default "website"; use "article" for recipes/guides)
 *   jsonLd      — Optional object or array → injected as <script type="application/ld+json">
 *   noIndex     — If true, adds <meta name="robots" content="noindex,nofollow">
 */
export default function Seo({
  title,
  description,
  keywords,
  image,
  path,
  type = "website",
  jsonLd,
  noIndex = false,
}) {
  useEffect(() => {
    // Title
    const fullTitle = title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} · Recettes IG bas pour diabétiques`;
    document.title = fullTitle;

    const setMeta = (name, content) => {
      if (content == null) return;
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", String(content));
    };
    const setOg = (prop, content) => {
      if (content == null) return;
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", prop);
        document.head.appendChild(el);
      }
      el.setAttribute("content", String(content));
    };
    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Description, keywords, robots
    setMeta("description", description);
    setMeta(
      "keywords",
      Array.isArray(keywords) ? keywords.join(", ") : keywords,
    );
    setMeta("robots", noIndex ? "noindex,nofollow" : "index,follow");

    // Canonical URL — must always be absolute https
    const pathname = path || window.location.pathname || "/";
    const canonicalUrl = `${SITE_URL}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;
    setLink("canonical", canonicalUrl);

    // Resolve image: if it's a data: URL or absolute http(s) URL, use as-is.
    // Otherwise prefix with SITE_URL. If no image is given, we don't set og:image
    // at all — crawlers fall back to no image.
    let resolvedImage = null;
    if (image) {
      if (image.startsWith("data:") || image.startsWith("http")) {
        resolvedImage = image;
      } else if (image.startsWith("/")) {
        resolvedImage = `${SITE_URL}${image}`;
      } else {
        resolvedImage = `${SITE_URL}/${image}`;
      }
    }

    // Open Graph
    setOg("og:title", title ? `${title} · ${SITE_NAME}` : `${SITE_NAME} · Recettes IG bas pour diabétiques`);
    setOg("og:description", description);
    setOg("og:type", type);
    setOg("og:url", canonicalUrl);
    setOg("og:site_name", SITE_NAME);
    setOg("og:locale", "fr_FR");
    // Only set og:image if NOT a data: URL — those are too heavy for crawlers
    if (resolvedImage && !resolvedImage.startsWith("data:")) {
      setOg("og:image", resolvedImage);
      setOg("og:image:alt", title || SITE_NAME);
    }

    // Twitter
    setMeta("twitter:card", resolvedImage && !resolvedImage.startsWith("data:") ? "summary_large_image" : "summary");
    setMeta("twitter:title", title ? `${title} · ${SITE_NAME}` : SITE_NAME);
    setMeta("twitter:description", description);
    if (resolvedImage && !resolvedImage.startsWith("data:")) {
      setMeta("twitter:image", resolvedImage);
    }

    // JSON-LD
    const scriptId = "rd-jsonld";
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();
    if (jsonLd) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = scriptId;
      s.text = JSON.stringify(jsonLd);
      document.head.appendChild(s);
    }

    return () => {
      const old = document.getElementById(scriptId);
      if (old) old.remove();
    };
  }, [title, description, keywords, image, path, type, jsonLd, noIndex]);

  return null;
}
